/**
 * Rechnungs-PDF ohne Fremdbibliothek (Befund Kommunikation 1): ein kleiner PDF-Schreiber (PDF 1.4, eine Seite oder
 * mehr, Standardschrift Helvetica mit WinAnsi-Kodierung, damit €, Umlaute und „…“ stimmen) und das Layout einer
 * Rechnung. Reines TypeScript, nur Uint8Array: läuft im Browser (Creator-Portal), in Deno (Edge Function) und in Node.
 *
 * Eingabe ist ein schlankes Modell (InvoicePdfModel). Es entsteht entweder aus dem Dokumentmodell
 * (buildInvoiceDocument → invoicePdfModelFromDocument) oder beim Versand aus der festgeschriebenen Rechnung
 * (packages/server, eingefrorene Parteien aus 0036 und gespeicherte Beträge). Keine Datenbankzeilen.
 * Pflichtangaben: Aussteller mit Anschrift und Steuernummer/USt-IdNr., Empfänger, Nummer, Datum, Leistungszeitraum,
 * Positionen, Netto, Steuersatz und -betrag, Brutto, Steuerhinweis, Fälligkeit, Bankverbindung. Beschriftung
 * zweisprachig wie InvoicePaper (apps/web/src/components/InvoicePaper.tsx).
 */
import { isSwissGerman, swissDeep } from "./swiss-spelling.js";
import type { BankDetails, InvoiceDocument } from "./document-model.js";
import { formatMoney } from "./money.js";
import { qrRuns } from "./qr-code.js";
import { BELEG_SCHRIFT_FAKTOR, belegLayout, belegLogoBox, girocodeInhalt, girocodeModule, hellerTon, type BelegLayout } from "./beleg-layout.js";
import {
  QR_BILL_LABELS, qrAccountDisplay, qrAddressLines, qrAmountDisplay, qrReferenceDisplay, swissQrCode, type SwissQrBill,
} from "./swiss-qr-bill.js";
import type { Cents, Currency, IsoDate, Language, MoneyLocale } from "./types.js";

// ---------------------------------------------------------------------------
// Modell
// ---------------------------------------------------------------------------

export interface InvoicePdfParty {
  name: string;
  addressLines: string[];
  /** Steuernummer, USt-IdNr. usw. mit Bezeichnung */
  fields?: { label: string; value: string }[];
}

export interface InvoicePdfLine {
  description: string;
  quantity: number;
  unitPriceCents: Cents;
  totalCents: Cents;
}

export interface InvoicePdfModel {
  language: Language;
  locale: MoneyLocale;
  currency: Currency;
  number: string;
  /** Entwurf: Kopf „ENTWURF“ statt „RECHNUNG“ */
  draft?: boolean;
  invoiceDate: IsoDate | null;
  /** Leistungsmonat 'YYYY-MM' (hat Vorrang) oder Leistungszeitraum */
  serviceMonth?: string | null;
  service?: { from: IsoDate; to: IsoDate } | null;
  dueDate: IsoDate | null;
  issuer: InvoicePdfParty;
  recipient: InvoicePdfParty;
  lines: InvoicePdfLine[];
  totals: {
    netCents: Cents;
    vatCents: Cents;
    grossCents: Cents;
    /** ein Satz in Prozent; null = mehrere Sätze oder keiner */
    ratePercent: number | null;
    /**
     * Steuerprüfung 23.10.2026, L2: bei mehreren Steuersätzen Entgelt und Steuer je Satz (§ 14 Abs. 4 Satz 1 Nr. 7 und 8 UStG);
     * je Satz eine Zeile statt der einen Steuerzeile. Fehlt oder hat weniger als zwei Einträge: wie bisher eine Zeile.
     */
    byRate?: readonly VatRateRow[] | null;
    /** K-280: ohne Steuerzeile (Aussteller in CH/LI nicht im MWST-Register: keine Zeile „MWST 0.00“) */
    hideVat?: boolean;
  };
  taxNotice?: string | null;
  bank?: BankDetails | null;
  /** zusätzliche Referenz der Marke (Bestellnummer o. Ä.) */
  reference?: string | null;
  /** Fußzeile: Firmenangaben des Ausstellers, je Eintrag eine Zeile */
  footerLines?: string[];
  /** Stornorechnung: hebt die genannte Rechnung auf (Kopf, Bezug, kein Zahlungsaufruf) */
  creditNote?: { forNumber: string; forDate: IsoDate | null; paidOutCents?: number | null } | null;
  /** Gutschrift (Abrechnung durch den Leistungsempfänger, z. B. § 14 Abs. 2 Satz 2 UStG): Kopf „GUTSCHRIFT“, Betrag wird ausgezahlt */
  selfBilling?: boolean;
  /** 0198: Beleg über Verzugskosten: der Zeitraum ist der Verzug, keine Leistung */
  lateCosts?: boolean;
  /**
   * Zahlungsziel in Tagen ab Zugang (0055, Rechnungen der Agentur): der Beleg nennt „zahlbar innerhalb von N Tagen nach Erhalt“
   * statt eines Datums, weil die Frist erst mit dem Zugang beginnt. Hat Vorrang vor `dueDate`.
   */
  paymentTermDays?: number | null;
  /** Bezeichnung der Steuerzeile (z. B. „MWST“ in der Schweiz); ohne Angabe Umsatzsteuer bzw. VAT */
  taxLabel?: string | null;
  /** Akzentfarbe der Agentur (#RRGGBB) für Kopf, Überschrift und Linien */
  accent?: string | null;
  /** Logo als JPEG (im Browser aus dem Logo der Agentur erzeugt); Breite und Höhe in Bildpunkten */
  logo?: PdfImage | null;
  /**
   * Schweizer QR-Zahlteil mit Empfangsschein (swiss-qr-bill.ts): unten auf der letzten Seite, sonst auf einer eigenen Seite.
   * Nur auf Belegen, deren Aussteller das Geld bekommt; nie auf Stornorechnungen und Gutschriften.
   */
  qrBill?: SwissQrBill | null;
  /** Hinweis statt Zahlteil (Entwurf: der Zahlteil entsteht mit der Nummer beim Festschreiben) */
  qrBillNote?: string | null;
  /**
   * 0267 (K-296): Umsatzsteuer in Euro bei fremder Rechnungswährung (Aussteller DE/AT, Art. 230 MwStSystRL), beim Festschreiben
   * eingefroren: Betrag, EZB-Referenzkurs (1 EUR = rate in Rechnungswährung) und dessen Tag
   */
  taxEur?: { cents: Cents; rate: number; on: IsoDate; source?: string | null } | null;
  /** PDF-Vorlage der Agentur (beleg-layout.ts, 0456); ohne Angabe wie bisher */
  layout?: unknown;
}

export interface VatRateRow {
  ratePercent: number;
  netCents: Cents;
  vatCents: Cents;
}

/**
 * L2: Aufschlüsselung nach Steuersätzen aus den Positionen (Satz in Basispunkten, ohne Angabe 0). Null bei höchstens einem Satz.
 * Steuer je Satz kaufmännisch gerundet; weicht die Summe nur um Rundungscent von der gespeicherten Steuer ab, trägt die Zeile mit
 * dem größten Entgelt den Unterschied, damit die Zeilen die ausgewiesene Steuer ergeben.
 */
export function vatBreakdown(lines: readonly { netCents: number; rateBp: number | null | undefined }[], vatCents: number): VatRateRow[] | null {
  const byRate = new Map<number, number>();
  for (const l of lines) {
    const bp = Number(l.rateBp ?? 0);
    byRate.set(bp, (byRate.get(bp) ?? 0) + Number(l.netCents));
  }
  if (byRate.size < 2) return null;
  const rows = [...byRate.entries()].sort((a, b) => a[0] - b[0]).map(([bp, net]) => ({
    ratePercent: bp / 100, netCents: net, vatCents: Math.sign(net) * Math.round(Math.abs(net) * bp / 10_000),
  }));
  const diff = Number(vatCents) - rows.reduce((s, r) => s + r.vatCents, 0);
  if (diff !== 0 && Math.abs(diff) <= rows.length) {
    const big = rows.filter((r) => r.ratePercent > 0).sort((a, b) => Math.abs(b.netCents) - Math.abs(a.netCents))[0];
    if (big) big.vatCents += diff;
  }
  return rows;
}

const LABELS = {
  de: {
    invoice: "RECHNUNG", draft: "ENTWURF", title: "Rechnung", number: "Rechnungsnummer", date: "Rechnungsdatum", month: "Leistungsmonat",
    period: "Leistungszeitraum", defaultPeriod: "Verzugszeitraum", due: "Zahlbar bis", reference: "Ihre Referenz", pos: "Pos.", item: "Leistung", qty: "Menge",
    unit: "Einzelpreis", amount: "Betrag", net: "Summe netto", vat: "Umsatzsteuer", gross: "Gesamtbetrag", noVat: "Umsatzsteuer",
    vatOn: (word: string, rate: string, net: string) => `${word} ${rate} auf ${net} netto`,
    pay: (amount: string, due: string | null, number: string) =>
      due ? `Bitte überweisen Sie den Gesamtbetrag von ${amount} bis zum ${due} unter Angabe der Rechnungsnummer ${number}.`
        : `Bitte überweisen Sie den Gesamtbetrag von ${amount} unter Angabe der Rechnungsnummer ${number}.`,
    payTerm: (amount: string, days: number, number: string) =>
      days <= 0 ? `Der Gesamtbetrag von ${amount} ist sofort nach Erhalt der Rechnung ohne Abzug zahlbar. Bitte geben Sie die Rechnungsnummer ${number} an.`
        : `Der Gesamtbetrag von ${amount} ist innerhalb von ${days} Tagen nach Erhalt der Rechnung ohne Abzug zahlbar. Bitte geben Sie die Rechnungsnummer ${number} an.`,
    payWallet: (amount: string, due: string | null, wallet: string, address: string, number: string) =>
      `Bitte zahlen Sie den Gesamtbetrag von ${amount}${due ? ` bis zum ${due}` : ""} per ${wallet} an ${address} unter Angabe der Rechnungsnummer ${number}.`,
    term: "Zahlungsziel", termValue: (days: number) => (days <= 0 ? "sofort nach Erhalt" : `${days} Tage nach Erhalt`),
    wallet: "Zahlung per", account: "Kontonummer", routing: "Bankleitzahl / Routing", bankCountry: "Land der Bank",
    bank: "Bankverbindung", holder: "Kontoinhaber", iban: "IBAN", bic: "BIC", bankName: "Bank", page: (n: number, of: number) => `Seite ${n} von ${of}`,
    continued: "Fortsetzung",
    creditNote: "STORNORECHNUNG", creditTitle: "Stornorechnung", cancels: "Storniert Rechnung", selfBilling: "GUTSCHRIFT", selfBillingTitle: "Gutschrift",
    selfBillingPay: (amount: string, due: string | null, hasAccount = true) =>
      hasAccount ? (due ? `Den Betrag von ${amount} überweisen wir bis zum ${due} auf das unten genannte Konto.` : `Den Betrag von ${amount} überweisen wir auf das unten genannte Konto.`)
        : (due ? `Den Betrag von ${amount} zahlen wir bis zum ${due} auf dem vereinbarten Weg aus.` : `Den Betrag von ${amount} zahlen wir auf dem vereinbarten Weg aus.`),
    selfBillingWallet: (amount: string, due: string | null, wallet: string, address: string) =>
      `Den Betrag von ${amount} zahlen wir${due ? ` bis zum ${due}` : ""} per ${wallet} an ${address} aus.`,
    payoutBy: "Auszahlung bis", payoutTo: "Auszahlung an",
    creditPay: (amount: string, number: string) =>
      `Diese Stornorechnung hebt die Rechnung ${number} vollständig auf. Ein bereits gezahlter Betrag von bis zu ${amount} wird erstattet oder verrechnet.`,
    // 0345: Stornobeleg zur Gutschrift (Gutschriftverfahren): Aussteller ist die Agentur, sie hat an den Creator gezahlt
    selfCreditNote: "STORNO GUTSCHRIFT", selfCreditTitle: "Storno-Gutschrift", selfCancels: "Storniert die Gutschrift",
    selfCreditPay: (amount: string, number: string) =>
      `Dieser Beleg storniert die Gutschrift ${number} vollständig. Ein bereits ausgezahlter Betrag von bis zu ${amount} wird zurückgefordert oder verrechnet.`,
    // Entscheidung 16.10.: die tatsächliche Rückforderung als Hinweiszeile
    selfCreditRecover: (paid: string, number: string) =>
      `Dieser Beleg storniert die Gutschrift ${number} vollständig. Rückforderung: Aus der Gutschrift wurden bereits ${paid} ausgezahlt; dieser Betrag wird zurückgefordert oder mit der nächsten Auszahlung verrechnet.`,
    selfCreditNothing: (number: string) =>
      `Dieser Beleg storniert die Gutschrift ${number} vollständig. Aus der Gutschrift wurde noch nichts ausgezahlt, es entsteht keine Rückforderung.`,
  },
  en: {
    invoice: "INVOICE", draft: "DRAFT", title: "Invoice", number: "Invoice no.", date: "Invoice date", month: "Service month",
    period: "Service period", defaultPeriod: "Default period", due: "Payable by", reference: "Your reference", pos: "No.", item: "Description", qty: "Qty",
    unit: "Unit price", amount: "Amount", net: "Net total", vat: "VAT", gross: "Total", noVat: "VAT",
    vatOn: (word: string, rate: string, net: string) => `${word} ${rate} on ${net} net`,
    pay: (amount: string, due: string | null, number: string) =>
      due ? `Please transfer the total amount of ${amount} by ${due}, quoting invoice number ${number}.`
        : `Please transfer the total amount of ${amount}, quoting invoice number ${number}.`,
    payTerm: (amount: string, days: number, number: string) =>
      days <= 0 ? `The total amount of ${amount} is payable immediately upon receipt of this invoice without deduction. Please quote invoice number ${number}.`
        : `The total amount of ${amount} is payable within ${days} days of receipt of this invoice without deduction. Please quote invoice number ${number}.`,
    payWallet: (amount: string, due: string | null, wallet: string, address: string, number: string) =>
      `Please pay the total amount of ${amount}${due ? ` by ${due}` : ""} via ${wallet} to ${address}, quoting invoice number ${number}.`,
    term: "Payment terms", termValue: (days: number) => (days <= 0 ? "due on receipt" : `${days} days after receipt`),
    wallet: "Payment via", account: "Account number", routing: "Bank code / routing", bankCountry: "Bank country",
    bank: "Bank details", holder: "Account holder", iban: "IBAN", bic: "BIC", bankName: "Bank", page: (n: number, of: number) => `Page ${n} of ${of}`,
    continued: "continued",
    creditNote: "CREDIT NOTE", creditTitle: "Credit note", cancels: "Cancels invoice", selfBilling: "SELF-BILLING INVOICE", selfBillingTitle: "Self-billing invoice",
    selfBillingPay: (amount: string, due: string | null, hasAccount = true) =>
      hasAccount ? (due ? `We will transfer the amount of ${amount} to the account below by ${due}.` : `We will transfer the amount of ${amount} to the account below.`)
        : (due ? `We will pay out the amount of ${amount} by ${due} via the agreed method.` : `We will pay out the amount of ${amount} via the agreed method.`),
    selfBillingWallet: (amount: string, due: string | null, wallet: string, address: string) =>
      `We will pay out the amount of ${amount}${due ? ` by ${due}` : ""} via ${wallet} to ${address}.`,
    payoutBy: "Payout by", payoutTo: "Payout to",
    creditPay: (amount: string, number: string) =>
      `This credit note fully cancels invoice ${number}. Any amount already paid, up to ${amount}, will be refunded or offset.`,
    selfCreditNote: "SELF-BILLING CANCELLATION", selfCreditTitle: "Self-billing cancellation", selfCancels: "Cancels self-billing invoice",
    selfCreditPay: (amount: string, number: string) =>
      `This document fully cancels self-billing invoice ${number}. Any amount already paid out, up to ${amount}, will be reclaimed or offset.`,
    selfCreditRecover: (paid: string, number: string) =>
      `This document fully cancels self-billing invoice ${number}. Amount to be reclaimed: ${paid} has already been paid out under it and will be reclaimed or offset against the next payout.`,
    selfCreditNothing: (number: string) =>
      `This document fully cancels self-billing invoice ${number}. Nothing has been paid out under it yet, so nothing is to be reclaimed.`,
  },
} as const;

/** Modell aus dem Dokumentmodell (Vorschau, Creator-Portal). Fußzeile und Referenz kann der Aufrufer ergänzen. */
export function invoicePdfModelFromDocument(doc: InvoiceDocument, extra: { currency: Currency; draft?: boolean; serviceMonth?: string | null; reference?: string | null; footerLines?: string[] }): InvoicePdfModel {
  return {
    language: doc.language,
    locale: doc.locale,
    currency: extra.currency,
    number: doc.number,
    ...(extra.draft !== undefined ? { draft: extra.draft } : {}),
    invoiceDate: doc.invoiceDate,
    serviceMonth: extra.serviceMonth ?? null,
    service: doc.service,
    dueDate: doc.dueDate,
    issuer: { name: doc.issuer.name, addressLines: doc.issuer.addressLines, fields: doc.issuer.fields.map((f) => ({ label: f.label, value: f.value })) },
    recipient: { name: doc.recipient.name, addressLines: doc.recipient.addressLines, fields: doc.recipient.fields.map((f) => ({ label: f.label, value: f.value })) },
    lines: doc.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents, totalCents: l.totalCents })),
    totals: { netCents: doc.totals.netCents, vatCents: doc.totals.vatCents, grossCents: doc.totals.grossCents, ratePercent: doc.totals.ratePercent },
    taxNotice: doc.taxNotice.text,
    bank: doc.bank,
    reference: extra.reference ?? null,
    footerLines: extra.footerLines ?? [],
  };
}

// ---------------------------------------------------------------------------
// Schrift: Helvetica und Helvetica-Bold, Breiten je WinAnsi-Zeichen (Adobe AFM, 1/1000 em)
// ---------------------------------------------------------------------------

type FontKey = "F1" | "F2";

/** Unicode → WinAnsi (CP1252) für die Zeichen zwischen 0x80 und 0x9F; 0xA0 … 0xFF sind Latin-1. */
const WIN_ANSI_EXTRA: Record<number, number> = {
  0x20ac: 0x80, 0x201a: 0x82, 0x0192: 0x83, 0x201e: 0x84, 0x2026: 0x85, 0x2020: 0x86, 0x2021: 0x87, 0x02c6: 0x88, 0x2030: 0x89,
  0x0160: 0x8a, 0x2039: 0x8b, 0x0152: 0x8c, 0x017d: 0x8e, 0x2018: 0x91, 0x2019: 0x92, 0x201c: 0x93, 0x201d: 0x94, 0x2022: 0x95,
  0x2013: 0x96, 0x2014: 0x97, 0x02dc: 0x98, 0x2122: 0x99, 0x0161: 0x9a, 0x203a: 0x9b, 0x0153: 0x9c, 0x017e: 0x9e, 0x0178: 0x9f,
  // Leerzeichen-Varianten aus Intl (schmales geschütztes Leerzeichen, schmales Leerzeichen)
  0x202f: 0xa0, 0x2009: 0x20, 0x2007: 0x20, 0x2002: 0x20, 0x2003: 0x20, 0x2212: 0x2d, 0x2011: 0x2d, 0x2010: 0x2d,
};

/**
 * Text einer Datei (DATEV, BMD) in Windows-1252: wie winAnsiBytes, aber Zeilenenden und Tabulatoren bleiben erhalten.
 * Praxistest K-111 (13.10.2026): Der Buchungsstapel kam als eine einzige Zeile an, weil winAnsiBytes (gebaut für eine
 * PDF-Zeile) CR und LF durch Leerzeichen ersetzt; so ließ sich die Datei nicht einlesen.
 */
export function winAnsiFileBytes(text: string): number[] {
  const out: number[] = [];
  for (const teil of text.split(/(\r\n|\r|\n|\t)/)) {
    if (teil === "\r\n") out.push(0x0d, 0x0a);
    else if (teil === "\r") out.push(0x0d);
    else if (teil === "\n") out.push(0x0a);
    else if (teil === "\t") out.push(0x09);
    else if (teil) out.push(...winAnsiBytes(teil));
  }
  return out;
}

/** Text in WinAnsi-Bytes; Zeichen außerhalb werden „?“ (nie ein kaputtes PDF). */
export function winAnsiBytes(text: string): number[] {
  const out: number[] = [];
  for (const ch of text.normalize("NFC")) {
    const cp = ch.codePointAt(0)!;
    if (cp === 0x09 || cp === 0x0a || cp === 0x0d) out.push(0x20);
    else if (cp >= 0x20 && cp <= 0x7e) out.push(cp);
    else if (cp >= 0xa0 && cp <= 0xff) out.push(cp);
    else if (WIN_ANSI_EXTRA[cp] !== undefined) out.push(WIN_ANSI_EXTRA[cp]!);
    else out.push(0x3f);
  }
  return out;
}

function widths(ascii: number[], extra: Record<number, number>): number[] {
  const w = new Array<number>(256).fill(0);
  ascii.forEach((v, i) => { w[32 + i] = v; });
  for (const [k, v] of Object.entries(extra)) w[Number(k)] = v;
  return w;
}
// 32 … 126
const HELV_ASCII = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
  278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667,
  611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833,
  556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];
const HELV_BOLD_ASCII = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
  333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, 667, 778, 722, 667,
  611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889,
  611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];
function high(regular: boolean): Record<number, number> {
  const r = regular;
  const m: Record<number, number> = {
    // Euro: die Standardmetrik sagt 556, die Helvetica in macOS/iOS zeichnet ihn 744 breit; mit 744 überlappt nichts
    0x80: 744, 0x82: r ? 222 : 278, 0x83: 556, 0x84: r ? 333 : 500, 0x85: 1000, 0x86: 556, 0x87: 556, 0x88: 333, 0x89: 1000,
    0x8a: 667, 0x8b: 333, 0x8c: 1000, 0x8e: 611, 0x91: r ? 222 : 278, 0x92: r ? 222 : 278, 0x93: r ? 333 : 500, 0x94: r ? 333 : 500,
    0x95: 350, 0x96: 556, 0x97: 1000, 0x98: 333, 0x99: 1000, 0x9a: r ? 500 : 556, 0x9b: 333, 0x9c: 944, 0x9e: 500, 0x9f: 667,
    0xa0: 278, 0xa1: 333, 0xa2: 556, 0xa3: 556, 0xa4: 556, 0xa5: 556, 0xa6: r ? 260 : 280, 0xa7: 556, 0xa8: 333, 0xa9: 737, 0xaa: 370,
    0xab: 556, 0xac: 584, 0xad: 333, 0xae: 737, 0xaf: 333, 0xb0: 400, 0xb1: 584, 0xb2: 333, 0xb3: 333, 0xb4: 333, 0xb5: r ? 556 : 611,
    0xb6: r ? 537 : 556, 0xb7: 278, 0xb8: 333, 0xb9: 333, 0xba: 365, 0xbb: 556, 0xbc: 834, 0xbd: 834, 0xbe: 834, 0xbf: 611,
    0xc6: 1000, 0xc7: 722, 0xd0: 722, 0xd1: 722, 0xd7: 584, 0xd8: 778, 0xdd: 667, 0xde: 667, 0xdf: 611,
    0xe6: 889, 0xe7: r ? 500 : 556, 0xf0: r ? 556 : 611, 0xf1: r ? 556 : 611, 0xf7: 584, 0xf8: 611, 0xfd: r ? 500 : 556, 0xfe: r ? 556 : 611, 0xff: r ? 500 : 556,
  };
  for (let c = 0xc0; c <= 0xc5; c++) m[c] = r ? 667 : 722;
  for (let c = 0xc8; c <= 0xcb; c++) m[c] = 667;
  for (let c = 0xcc; c <= 0xcf; c++) m[c] = 278;
  for (let c = 0xd2; c <= 0xd6; c++) m[c] = 778;
  for (let c = 0xd9; c <= 0xdc; c++) m[c] = 722;
  for (let c = 0xe0; c <= 0xe5; c++) m[c] = 556;
  for (let c = 0xe8; c <= 0xeb; c++) m[c] = 556;
  for (let c = 0xec; c <= 0xef; c++) m[c] = 278;
  for (let c = 0xf2; c <= 0xf6; c++) m[c] = r ? 556 : 611;
  for (let c = 0xf9; c <= 0xfc; c++) m[c] = r ? 556 : 611;
  return m;
}
const WIDTHS: Record<FontKey, number[]> = { F1: widths(HELV_ASCII, high(true)), F2: widths(HELV_BOLD_ASCII, high(false)) };

/** Breite eines Textes in Punkt. */
export function textWidth(text: string, font: FontKey, size: number): number {
  const w = WIDTHS[font];
  return winAnsiBytes(text).reduce((s, b) => s + (w[b] || 556), 0) * size / 1000;
}

/** Text auf eine Breite umbrechen (an Leerzeichen; überlange Wörter hart). */
export function wrapText(text: string, font: FontKey, size: number, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.replace(/\r\n?/g, "\n").split("\n")) {
    const words = para.split(/ +/).filter((x) => x.length > 0);
    let line = "";
    for (const word of words) {
      const cand = line ? `${line} ${word}` : word;
      if (textWidth(cand, font, size) <= maxWidth) { line = cand; continue; }
      if (line) out.push(line);
      if (textWidth(word, font, size) <= maxWidth) { line = word; continue; }
      // Wort länger als die Zeile: zeichenweise teilen
      let part = "";
      for (const ch of word) {
        if (textWidth(part + ch, font, size) > maxWidth && part) { out.push(part); part = ch; } else part += ch;
      }
      line = part;
    }
    out.push(line);
  }
  return out;
}

// ---------------------------------------------------------------------------
// PDF-Schreiber
// ---------------------------------------------------------------------------

export type Rgb = readonly [number, number, number];
export const INK: Rgb = [0.125, 0.153, 0.141];      // #202724
export const MUTED: Rgb = [0.341, 0.396, 0.369];    // #57655E
export const RULE: Rgb = [0.847, 0.875, 0.855];     // #D8DFDA

/** PDF-Zeichenkette: WinAnsi-Bytes, Sonderzeichen und alles über 0x7E als Oktal-Escape (die Datei bleibt ASCII). */
function pdfString(text: string): string {
  let s = "(";
  for (const b of winAnsiBytes(text)) {
    if (b === 0x28 || b === 0x29 || b === 0x5c) s += `\\${String.fromCharCode(b)}`;
    else if (b < 0x20 || b > 0x7e) s += `\\${b.toString(8).padStart(3, "0")}`;
    else s += String.fromCharCode(b);
  }
  return `${s})`;
}
/** Metadaten (Titel): UTF-16BE mit Byte-Order-Mark, damit Umlaute im Dokumenttitel stimmen. */
function pdfUnicode(text: string): string {
  let hex = "FEFF";
  for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).padStart(4, "0").toUpperCase();
  return `<${hex}>`;
}
const n = (v: number) => (Math.round(v * 100) / 100).toString();

/**
 * Zeichenbefehl einer Seite in Klartext (K-410): dieselben Befehle wie im PDF-Inhaltsstrom, damit die Vorschau im Portal
 * (pagesToSvg) aus genau denselben Angaben entsteht wie das versendete PDF.
 */
export type PageItem =
  | { k: "text"; x: number; y: number; str: string; font: FontKey; size: number; color: Rgb }
  | { k: "line"; x1: number; y1: number; x2: number; y2: number; width: number; color: Rgb; dash?: readonly [number, number] }
  | { k: "rect"; x: number; y: number; w: number; h: number; color: Rgb }
  | { k: "rects"; list: readonly (readonly [number, number, number, number])[]; color: Rgb }
  | { k: "image"; name: string; x: number; y: number; w: number; h: number };

export class Page {
  ops: string[] = [];
  items: PageItem[] = [];
  text(x: number, y: number, str: string, font: FontKey = "F1", size = 10, color: Rgb = INK) {
    if (!str) return;
    this.ops.push(`BT /${font} ${n(size)} Tf ${color.map(n).join(" ")} rg ${n(x)} ${n(y)} Td ${pdfString(str)} Tj ET`);
    this.items.push({ k: "text", x, y, str, font, size, color });
  }
  right(xRight: number, y: number, str: string, font: FontKey = "F1", size = 10, color: Rgb = INK) {
    this.text(xRight - textWidth(str, font, size), y, str, font, size, color);
  }
  line(x1: number, y1: number, x2: number, y2: number, width = 0.5, color: Rgb = RULE, dash?: readonly [number, number]) {
    const d = dash ? `[${n(dash[0])} ${n(dash[1])}] 0 d ` : "";
    this.ops.push(`${d}${color.map(n).join(" ")} RG ${n(width)} w ${n(x1)} ${n(y1)} m ${n(x2)} ${n(y2)} l S${dash ? " [] 0 d" : ""}`);
    this.items.push({ k: "line", x1, y1, x2, y2, width, color, ...(dash ? { dash } : {}) });
  }
  /** Gefülltes Rechteck (x, y = linke untere Ecke) */
  rect(x: number, y: number, w: number, h: number, color: Rgb = INK) {
    this.ops.push(`${color.map(n).join(" ")} rg ${n(x)} ${n(y)} ${n(w)} ${n(h)} re f`);
    this.items.push({ k: "rect", x, y, w, h, color });
  }
  /** Viele Rechtecke als EIN Pfad gefüllt (QR-Code: keine Haarlinien zwischen Nachbarmodulen) */
  rects(list: readonly (readonly [number, number, number, number])[], color: Rgb = INK) {
    if (!list.length) return;
    const n3 = (v: number) => (Math.round(v * 1000) / 1000).toString();
    this.ops.push(`${color.map(n).join(" ")} rg ${list.map(([x, y, w, h]) => `${n3(x)} ${n3(y)} ${n3(w)} ${n3(h)} re`).join(" ")} f`);
    this.items.push({ k: "rects", list, color });
  }
  /** Bild /ImN an Position (x, y = Unterkante) in Breite w und Höhe h Punkt */
  image(name: string, x: number, y: number, w: number, h: number) {
    this.ops.push(`q ${n(w)} 0 0 ${n(h)} ${n(x)} ${n(y)} cm /${name} Do Q`);
    this.items.push({ k: "image", name, x, y, w, h });
  }
}

/** Bild für assemble: JPEG-Bytes mit Pixelmaßen (Logo) */
export interface PdfImage { jpeg: Uint8Array; width: number; height: number }

/** #RRGGBB → Farbe für den PDF-Schreiber; zu helle Farben (schlecht lesbar auf Weiß) fallen auf die Schriftfarbe zurück */
export function pdfAccent(hex: string | null | undefined): Rgb {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex?.trim() ?? "");
  if (!m) return INK;
  const v = parseInt(m[1]!, 16);
  const rgb: Rgb = [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
  const lum = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  return lum > 0.75 ? INK : rgb;
}

/**
 * Agenturfarbe für Belege in der Vorschau (HTML) und im Vertrags-PDF: dieselbe Helligkeitsgrenze wie pdfAccent. Zu helle
 * oder ungültige Farben ergeben null, die Stelle bleibt dann neutral (statt gelb auf weißem Papier).
 */
export function belegAkzent(hex: string | null | undefined): string | null {
  const v = hex?.trim() ?? "";
  if (!/^#[0-9a-f]{6}$/i.test(v)) return null;
  return pdfAccent(v) === INK ? null : v;
}

/** Farben eines Belegs nach PDF-Vorlage (0456): Überschrift, Linien, Füllung des Designs „akzent“ */
export function belegFarben(lay: BelegLayout, accentHex: string | null | undefined): { marke: Rgb; titel: Rgb; linie: Rgb; fuellung: Rgb | null } {
  const marke = pdfAccent(lay.akzent ?? accentHex);
  return { marke, titel: lay.design === "minimal" ? INK : marke, linie: lay.design === "minimal" ? RULE : marke, fuellung: lay.design === "akzent" ? hellerTon(marke) : null };
}

/** Logo nach PDF-Vorlage oben links oder rechts; gibt die nächste freie Grundlinie zurück */
export function belegLogo(page: Page, lay: BelegLayout, logo: PdfImage | null | undefined, y: number): { y: number; logo: PdfImage | null } {
  const bild = lay.logo.zeigen && logo && logo.width > 0 && logo.height > 0 ? logo : null;
  if (!bild) return { y, logo: null };
  const box = belegLogoBox(lay.logo.groesse);
  const s = Math.min(box.w / bild.width, box.h / bild.height);
  const w = bild.width * s, h = bild.height * s;
  page.image("Im1", lay.logo.position === "rechts" ? R - w : L, y - h + 12, w, h);
  return { y: y - h - 10, logo: bild };
}

/** Girocode (EPC-QR) rechts an der Grundlinie y zeichnen (Kantenlänge size), mit kurzer Beschriftung darunter */
export function belegGirocode(page: Page, inhalt: string, y: number, size: number, lang: Language) {
  const q = girocodeModule(inhalt);
  const s = size / q.size, gx = R - size, gy = y - size + 9;
  page.rects(q.runs.map(([x, yy, w]) => [gx + x * s, gy + (q.size - 1 - yy) * s, w * s, s] as const), INK);
  page.right(R, gy - 10, lang === "en" ? "Scan to pay (GiroCode)" : "Mit der Banking-App scannen", "F1", 7.5, MUTED);
}

/** Setzt die Objekte zu einer PDF-Datei zusammen (Katalog, Seiten, zwei Schriften, Inhalte, Metadaten, Querverweise). */
export function assemble(pages: Page[], title: string, created: Date, images: PdfImage[] = []): Uint8Array {
  const objs: string[] = [];
  const add = (body: string) => { objs.push(body); return objs.length; };
  const catalog = add("");           // 1, unten gefüllt
  const pagesObj = add("");          // 2
  // Breiten mitgeben, damit jeder Betrachter (auch mit Ersatzschrift) dieselben Abstände und rechtsbündigen Beträge setzt
  const font = (base: string, key: FontKey) =>
    `<< /Type /Font /Subtype /Type1 /BaseFont /${base} /Encoding /WinAnsiEncoding /FirstChar 32 /LastChar 255 /Widths [${WIDTHS[key].slice(32).map((w) => w || 556).join(" ")}] >>`;
  const f1 = add(font("Helvetica", "F1"));
  const f2 = add(font("Helvetica-Bold", "F2"));
  // Bilder (JPEG, DCTDecode): der Inhalt steht als Binärstrom zwischen den Textobjekten
  const binaries = new Map<number, Uint8Array>();
  const xobj: string[] = [];
  images.forEach((img, i) => {
    const id = add(`<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.jpeg.length} >>\nstream\n@@BIN@@\nendstream`);
    binaries.set(id, img.jpeg);
    xobj.push(`/Im${i + 1} ${id} 0 R`);
  });
  const res = `/Font << /F1 ${f1} 0 R /F2 ${f2} 0 R >>${xobj.length ? ` /XObject << ${xobj.join(" ")} >>` : ""}`;
  const kids: number[] = [];
  for (const p of pages) {
    const stream = p.ops.join("\n");
    const content = add(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    kids.push(add(`<< /Type /Page /Parent ${pagesObj} 0 R /MediaBox [0 0 595.28 841.89] /Resources << ${res} >> /Contents ${content} 0 R >>`));
  }
  objs[catalog - 1] = `<< /Type /Catalog /Pages ${pagesObj} 0 R >>`;
  objs[pagesObj - 1] = `<< /Type /Pages /Kids [${kids.map((k) => `${k} 0 R`).join(" ")}] /Count ${kids.length} >>`;
  const d = created.toISOString().replace(/[-:T]/g, "").slice(0, 14);
  const info = add(`<< /Title ${pdfUnicode(title)} /Producer (CreatorOffice) /CreationDate (D:${d}Z) >>`);

  // Kopf mit Binärkennung (vier Bytes über 0x7F); alles danach ist ASCII, Länge in Zeichen = Länge in Bytes
  const header = new Uint8Array([...Array.from("%PDF-1.4\n%", (c) => c.charCodeAt(0)), 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]);
  const ascii = (str: string) => {
    const b = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) b[i] = str.charCodeAt(i) & 0xff;
    return b;
  };
  const parts: Uint8Array[] = [header];
  let offset = header.length;
  const offsets: number[] = [];
  objs.forEach((body, i) => {
    offsets.push(offset);
    const bin = binaries.get(i + 1);
    const chunks: Uint8Array[] = [];
    if (bin) {
      const [before, after] = body.split("@@BIN@@") as [string, string];
      chunks.push(ascii(`${i + 1} 0 obj\n${before}`), bin, ascii(`${after}\nendobj\n`));
    } else {
      chunks.push(ascii(`${i + 1} 0 obj\n${body}\nendobj\n`));
    }
    for (const c of chunks) { parts.push(c); offset += c.length; }
  });
  const xref = offset;
  parts.push(ascii(`xref\n0 ${objs.length + 1}\n0000000000 65535 f \n${offsets.map((o) => `${String(o).padStart(10, "0")} 00000 n \n`).join("")}`
    + `trailer\n<< /Size ${objs.length + 1} /Root ${catalog} 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`));
  const all = new Uint8Array(parts.reduce((a, b) => a + b.length, 0));
  let pos = 0;
  for (const p of parts) { all.set(p, pos); pos += p.length; }
  return all;
}

// ---------------------------------------------------------------------------
// Layout der Rechnung
// ---------------------------------------------------------------------------

const W = 595.28, H = 841.89, L = 56, R = W - 56;
const FOOTER_TOP = 78;
/** Seitenmaße des PDF-Schreibers (A4, Ränder), für weitere Belege wie das Mahnschreiben */
export const PDF_LAYOUT = { W, H, L, R, FOOTER_TOP } as const;

/** Fußzeilen des PDF-Schreibers umgebrochen (K-423): höchstens acht Zeilen, nichts wird still abgeschnitten */
export function footerWrapped(lines: readonly string[], width: number): string[] {
  return lines.map((x) => x.trim()).filter(Boolean).flatMap((x) => wrapText(x, "F1", 7.5, width)).slice(0, 8);
}

/** Text, wie ihn das PDF zeigt: Zeichen außerhalb von WinAnsi werden dort zu „?“, Zeilenumbrüche zu Leerzeichen */
function pdfVisibleText(str: string): string {
  const bytes = winAnsiBytes(str);
  try {
    return new TextDecoder("windows-1252").decode(new Uint8Array(bytes));
  } catch {
    return String.fromCharCode(...bytes);
  }
}

const svgEsc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const svgNum = (v: number) => (Math.round(v * 100) / 100).toString();
const svgColor = (c: Rgb) => `rgb(${c.map((v) => Math.round(v * 255)).join(",")})`;

/**
 * K-410: Seiten des PDF-Schreibers als SVG (eine Zeichenkette je Seite, A4 595,28 × 841,89 pt). Dieselben Zeichenbefehle wie im
 * PDF; jede Textzeile bekommt die im PDF berechnete Breite (textLength), damit Umbrüche, rechtsbündige Beträge und Abstände
 * auch mit einer Ersatzschrift genau so stehen wie im versendeten Dokument. Bilder (Logo) als eingebettetes JPEG.
 */
export function pagesToSvg(pages: readonly Page[], images: readonly PdfImage[] = []): string[] {
  const href = new Map<string, string>();
  images.forEach((img, i) => href.set(`Im${i + 1}`, `data:image/jpeg;base64,${bytesToBase64(img.jpeg)}`));
  const Y = (y: number) => svgNum(H - y);
  return pages.map((p) => {
    const out: string[] = [];
    for (const it of p.items) {
      if (it.k === "text") {
        const str = pdfVisibleText(it.str);
        if (!str) continue;
        const w = textWidth(it.str, it.font, it.size);
        const fit = str.trim().length > 1 && w > 0 ? ` textLength="${svgNum(w)}" lengthAdjust="spacingAndGlyphs"` : "";
        out.push(`<text x="${svgNum(it.x)}" y="${Y(it.y)}" font-size="${svgNum(it.size)}"${it.font === "F2" ? ' font-weight="700"' : ""} fill="${svgColor(it.color)}"${fit}>${svgEsc(str)}</text>`);
      } else if (it.k === "line") {
        out.push(`<line x1="${svgNum(it.x1)}" y1="${Y(it.y1)}" x2="${svgNum(it.x2)}" y2="${Y(it.y2)}" stroke="${svgColor(it.color)}" stroke-width="${svgNum(it.width)}"${it.dash ? ` stroke-dasharray="${svgNum(it.dash[0])} ${svgNum(it.dash[1])}"` : ""}/>`);
      } else if (it.k === "rect") {
        out.push(`<rect x="${svgNum(it.x)}" y="${svgNum(H - it.y - it.h)}" width="${svgNum(it.w)}" height="${svgNum(it.h)}" fill="${svgColor(it.color)}"/>`);
      } else if (it.k === "rects") {
        const d = it.list.map(([x, y, w, h]) => `M${svgNum(x)} ${svgNum(H - y - h)}h${svgNum(w)}v${svgNum(h)}h${svgNum(-w)}z`).join("");
        out.push(`<path d="${d}" fill="${svgColor(it.color)}" shape-rendering="crispEdges"/>`);
      } else if (it.k === "image") {
        const src = href.get(it.name);
        if (src) out.push(`<image href="${src}" x="${svgNum(it.x)}" y="${svgNum(H - it.y - it.h)}" width="${svgNum(it.w)}" height="${svgNum(it.h)}" preserveAspectRatio="none"/>`);
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgNum(W)} ${svgNum(H)}" width="${svgNum(W)}" height="${svgNum(H)}">`
      + `<rect width="100%" height="100%" fill="#ffffff"/>`
      + `<g font-family="Helvetica, Arial, 'Liberation Sans', sans-serif" xml:space="preserve">${out.join("")}</g></svg>`;
  });
}

export function fmtDate(iso: string | null | undefined, lang: Language, locale: MoneyLocale): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  if (lang === "en") return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }).format(d);
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(d);
}
export function fmtMonth(key: string, lang: Language, locale: MoneyLocale): string {
  const d = new Date(`${key.slice(0, 7)}-01T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return key;
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(d);
}
function fmtQty(q: number, lang: Language, locale: MoneyLocale): string {
  return new Intl.NumberFormat(lang === "en" ? "en-GB" : locale, { maximumFractionDigits: 3 }).format(q);
}
function fmtRate(r: number, lang: Language): string {
  const s = new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }).format(r);
  return lang === "en" ? `${s}%` : `${s} %`;
}
export function groupIban(iban: string): string {
  return iban.replace(/\s+/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();
}

/** Rechnung als PDF. `now` nur für reproduzierbare Tests (Erstellungsdatum in den Metadaten). */
/**
 * Texte eines Belegs aus dem Modell: dieselben Wörter für PDF und Ansicht im Portal (InvoicePaper). Kopf, Angaben rechts,
 * Steuerzeile, Steuerhinweis, Zahlungsaufruf und Bank- bzw. Auszahlungsblock.
 */
/** K-452: Belege von Agenturen in der Schweiz und Liechtenstein (de-CH, de-LI) ohne „ß“; der QR-Zahlteil bleibt unverändert */
function swissModel(model: InvoicePdfModel): InvoicePdfModel {
  return model.language !== "en" && isSwissGerman(model.locale) ? swissDeep(model, ["qrBill", "logo"]) : model;
}

export function invoiceTexts(model0: InvoicePdfModel) {
  const model = swissModel(model0);
  const lang: Language = model.language === "en" ? "en" : "de";
  const t = model === model0 ? LABELS[lang] : swissDeep(LABELS[lang]);
  const m = (c: Cents) => formatMoney(c, model.currency, model.locale);
  const credit = model.creditNote ?? null;
  const self = !credit && !!model.selfBilling;
  // 0345: Storno einer Gutschrift heißt nicht „Stornorechnung“ und verweist auf die Gutschrift
  const selfCredit = !!credit && !!model.selfBilling;
  const titleWord = selfCredit ? t.selfCreditTitle : credit ? t.creditTitle : self ? t.selfBillingTitle : t.title;
  const head = model.draft ? t.draft : selfCredit ? t.selfCreditNote : credit ? t.creditNote : self ? t.selfBilling : t.invoice;
  const meta: [string, string][] = [[t.number, model.number || "–"]];
  const inv = fmtDate(model.invoiceDate, lang, model.locale);
  if (inv) meta.push([t.date, inv]);
  if (model.serviceMonth && /^\d{4}-\d{2}/.test(model.serviceMonth)) meta.push([t.month, fmtMonth(model.serviceMonth, lang, model.locale)]);
  else if (model.service) {
    const a = fmtDate(model.service.from, lang, model.locale), b = fmtDate(model.service.to, lang, model.locale);
    if (a) meta.push([model.lateCosts ? t.defaultPeriod : t.period, a === b || !b ? a : `${a} – ${b}`]);
  }
  if (credit) {
    const fd = fmtDate(credit.forDate, lang, model.locale);
    meta.push([selfCredit ? t.selfCancels : t.cancels, fd ? `${credit.forNumber} · ${fd}` : credit.forNumber]);
  }
  const term = !credit && !self && typeof model.paymentTermDays === "number" ? Math.max(0, Math.round(model.paymentTermDays)) : null;
  const due = credit || term !== null ? null : fmtDate(model.dueDate, lang, model.locale);
  if (term !== null) meta.push([t.term, t.termValue(term)]);
  else if (due) meta.push([self ? t.payoutBy : t.due, due]);
  if (model.reference?.trim()) meta.push([t.reference, model.reference.trim()]);
  const taxWord = model.taxLabel?.trim() || t.vat;
  const taxRowLabel = model.totals.ratePercent !== null && model.totals.ratePercent > 0
    ? `${taxWord} ${fmtRate(model.totals.ratePercent, lang)}` : model.taxLabel?.trim() || t.noVat;
  // L2: je Steuersatz eine Zeile mit Satz, Entgelt und Steuer; ein Satz: die eine Steuerzeile wie bisher
  const byRate = model.totals.byRate && model.totals.byRate.length > 1 ? model.totals.byRate : null;
  const taxRows: [string, Cents][] = model.totals.hideVat ? []
    : byRate ? byRate.map((r) => [t.vatOn(taxWord, fmtRate(r.ratePercent, lang), m(r.netCents)), r.vatCents])
      : [[taxRowLabel, model.totals.vatCents]];
  const notice = model.taxNotice?.replace(/<[^>]+>/g, "").trim() ?? "";
  const bank = credit ? null : model.bank;
  // Zahlungsweg: PayPal oder Wise mit Adresse statt Kontoverbindung (Befund Creator 15)
  const wallet = bank && (bank.method === "paypal" || bank.method === "wise") && bank.email?.trim()
    ? { name: bank.method === "paypal" ? "PayPal" : "Wise", address: bank.email.trim() } : null;
  const bankRows: [string, string][] = [];
  if (wallet) {
    bankRows.push([t.wallet, `${wallet.name} · ${wallet.address}`]);
    if (bank?.accountHolder?.trim()) bankRows.push([t.holder, bank.accountHolder.trim()]);
  } else {
    if (bank?.accountHolder?.trim()) bankRows.push([t.holder, bank.accountHolder.trim()]);
    if (bank?.iban?.trim()) bankRows.push([t.iban, groupIban(bank.iban)]);
    if (bank?.accountNumber?.trim() && !bank.iban?.trim()) bankRows.push([t.account, bank.accountNumber.trim()]);
    if (bank?.routingCode?.trim()) bankRows.push([t.routing, bank.routingCode.trim()]);
    if (bank?.bic?.trim()) bankRows.push([t.bic, bank.bic.replace(/\s+/g, "").toUpperCase()]);
    if (bank?.bankName?.trim()) bankRows.push([t.bankName, bank.bankName.trim()]);
    if (bank?.bankCountry?.trim() && bank.method === "swift") bankRows.push([t.bankCountry, bank.bankCountry.trim()]);
  }
  const paidOut = selfCredit && typeof credit!.paidOutCents === "number" ? Math.min(Math.abs(credit!.paidOutCents), Math.abs(model.totals.grossCents)) : null;
  const payText = selfCredit && paidOut !== null ? (paidOut > 0 ? t.selfCreditRecover(m(paidOut), credit!.forNumber) : t.selfCreditNothing(credit!.forNumber))
    : selfCredit ? t.selfCreditPay(m(Math.abs(model.totals.grossCents)), credit!.forNumber)
    : credit ? t.creditPay(m(Math.abs(model.totals.grossCents)), credit.forNumber)
    : self && wallet ? t.selfBillingWallet(m(model.totals.grossCents), due, wallet.name, wallet.address)
    : self ? t.selfBillingPay(m(model.totals.grossCents), due, bankRows.length > 0)
      : wallet ? t.payWallet(m(model.totals.grossCents), due, wallet.name, wallet.address, model.number)
        : term !== null ? t.payTerm(m(model.totals.grossCents), term, model.number)
          : t.pay(m(model.totals.grossCents), due, model.number);
  const qrBill = !credit && !self && model.qrBill ? model.qrBill : null;
  const fx = model.taxEur && model.currency !== "EUR" && Number(model.taxEur.rate) > 0 ? model.taxEur : null;
  const fxDate = fx ? fmtDate(fx.on, lang, model.locale) : null;
  const fxRate = fx ? Number(fx.rate).toLocaleString(lang === "en" ? "en-GB" : "de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "";
  // K-330: die tatsächliche Quelle des Kurses (EZB oder eigene Angabe des Ausstellers)
  const ezb = !fx?.source || fx.source === "EZB";
  // K-396 (0298): BMF-Monatskurs des Leistungsmonats (§ 16 Abs. 6 UStG) gilt für den ganzen Monat, deshalb ohne Kurstag
  const bmf = !!fx?.source && /^BMF-Umrechnungskurs /.test(fx.source);
  const taxEurText = fx && bmf
    ? lang === "en"
      ? `VAT in EUR: ${formatMoney(fx.cents, "EUR", model.locale)} (converted at the monthly VAT conversion rate of the German Federal Ministry of Finance for ${fmtMonth(fx.on, "en", model.locale)}, § 16(6) UStG: 1 EUR = ${fxRate} ${model.currency})`
      : `Umsatzsteuer in EUR: ${formatMoney(fx.cents, "EUR", model.locale)} (umgerechnet mit dem ${fx.source}, § 16 Abs. 6 UStG: 1 EUR = ${fxRate} ${model.currency})`
    : fx
    ? lang === "en"
      ? `VAT in EUR: ${formatMoney(fx.cents, "EUR", model.locale)} (converted at ${ezb ? "the ECB reference rate" : `the rate “${fx.source}”`} of ${fxDate}: 1 EUR = ${fxRate} ${model.currency})`
      : `Umsatzsteuer in EUR: ${formatMoney(fx.cents, "EUR", model.locale)} (umgerechnet mit ${ezb ? "dem EZB-Referenzkurs" : `dem Kurs „${fx.source}“`} vom ${fxDate}: 1 EUR = ${fxRate} ${model.currency})`
    : "";
  return {
    lang, t, m, credit, self, titleWord, head, meta, taxRowLabel, taxRows, notice, bankRows, qrBill, taxEurText,
    payText,
    bankTitle: self ? t.payoutTo : t.bank,
    qty: (q: number) => fmtQty(q, lang, model.locale),
  };
}

export function renderInvoicePdf(model: InvoicePdfModel, opts: { now?: Date } = {}): Uint8Array {
  const l = layoutInvoicePdf(model);
  return assemble(l.pages, l.title, opts.now ?? new Date(), l.images);
}

/** K-410: Rechnung als Bild je Seite (SVG) aus demselben Layout wie das PDF, für die Vorschau im Mahnfenster */
export function renderInvoiceSvg(model: InvoicePdfModel): string[] {
  const l = layoutInvoicePdf(model);
  return pagesToSvg(l.pages, l.images);
}

function layoutInvoicePdf(model0: InvoicePdfModel): { pages: Page[]; title: string; images: PdfImage[] } {
  const model = swissModel(model0);
  const tx = invoiceTexts(model0);
  const { lang, t: t0, m, credit, self } = tx;
  void self;
  // PDF-Vorlage (0456): Design, Schriftgröße, Farbe, Logo, Spalten und Beschriftungen, zusätzliche Texte; ohne Vorlage wie bisher
  // Gutschrift im Namen des Creators (Prüf-Chat P2): keine Texte der Agentur (Hinweis, Bedingungen, Fuß, eigener Titel)
  const lay0 = belegLayout(model0.layout);
  const lay = model0.selfBilling ? { ...lay0, hinweis: null, bedingungen: null, fusstext: null, titel: null } : lay0;
  const k = BELEG_SCHRIFT_FAKTOR[lay.schrift];
  const fs = (v: number) => Math.round(v * k * 100) / 100;
  const b = lay.beschriftung;
  const t = { ...t0, pos: b.pos ?? t0.pos, item: b.leistung ?? t0.item, qty: b.menge ?? t0.qty, unit: b.einzelpreis ?? t0.unit, amount: b.betrag ?? t0.amount };
  const eigenerTitel = lay.titel && !credit && !self && !model.draft && !model.lateCosts ? lay.titel : null;
  const titleWord = eigenerTitel ?? tx.titleWord;
  const head = eigenerTitel ? eigenerTitel.toUpperCase() : tx.head;
  const markenFarbe = pdfAccent(lay.akzent ?? model.accent);
  const accent = lay.design === "minimal" ? INK : markenFarbe;
  const linie = lay.design === "minimal" ? RULE : markenFarbe;
  const pages: Page[] = [];
  let page = new Page();
  pages.push(page);
  let y = H - 56;
  // Design „akzent“: farbiges Band am oberen Rand jeder Seite (am Ende gezeichnet)

  // K-423: Fußzeilen umbrechen statt abschneiden; ab der fünften Zeile wächst der Fuß nach oben
  const footer = footerWrapped([...(model.footerLines ?? []), ...(lay.fusstext ? [lay.fusstext] : [])], R - L - 70);
  const footTop = FOOTER_TOP + Math.max(0, footer.length - 4) * 10;
  const bottom = footTop + 12 + Math.min(4, footer.length) * 10;

  // ── Logo der Agentur (Rahmen je Größe der Vorlage, bisher 150 × 48 Punkt) über dem Kopf, links oder rechts ──
  const logo = lay.logo.zeigen && model.logo && model.logo.width > 0 && model.logo.height > 0 ? model.logo : null;
  if (logo) {
    const box = belegLogoBox(lay.logo.groesse);
    const scale = Math.min(box.w / logo.width, box.h / logo.height);
    const w = logo.width * scale, h = logo.height * scale;
    page.image("Im1", lay.logo.position === "rechts" ? R - w : L, y - h + 12, w, h);
    y -= h + 10;
  }

  // ── Kopf: Aussteller links, RECHNUNG rechts ──
  const issuerName = model.issuer.name.trim() || "–";
  for (const [i, part] of wrapText(issuerName, "F2", fs(15), R - L - 160).entries()) page.text(L, y - i * fs(18), part, "F2", fs(15));
  page.right(R, y, head, "F2", fs(18), accent);
  y -= fs(18) * Math.max(1, wrapText(issuerName, "F2", fs(15), R - L - 160).length) - 4;
  const issuerLine = model.issuer.addressLines.map((x) => x.trim()).filter(Boolean).join(" · ");
  if (lay.kopfAdresse) for (const part of wrapText(issuerLine, "F1", fs(9), R - L)) { page.text(L, y, part, "F1", fs(9), MUTED); y -= fs(12); }
  for (const f of model.issuer.fields ?? []) { page.text(L, y, `${f.label}: ${f.value}`, "F1", fs(9), MUTED); y -= fs(12); }
  y -= 18;

  // ── Empfänger links, Rechnungsangaben rechts ──
  const top = Math.min(y, H - 150);
  let ly = top;
  const sender = [issuerName, ...model.issuer.addressLines].map((x) => x.trim()).filter(Boolean).join(" · ");
  // K-423: Absenderzeile vollständig, bei Bedarf in zwei Zeilen (unterstrichen wird die letzte, über dem Empfänger)
  const senderLines = wrapText(sender, "F1", 7, 250);
  senderLines.forEach((part, i) => page.text(L, ly - i * 9, part, "F1", 7, MUTED));
  const lastSender = senderLines[senderLines.length - 1] ?? "";
  ly -= Math.max(0, senderLines.length - 1) * 9;
  page.line(L, ly - 2, L + textWidth(lastSender, "F1", 7), ly - 2, 0.3, MUTED);
  ly -= 18;
  for (const part of wrapText(model.recipient.name.trim() || "–", "F2", fs(11), 250)) { page.text(L, ly, part, "F2", fs(11)); ly -= fs(14); }
  for (const a of model.recipient.addressLines.map((x) => x.trim()).filter(Boolean)) {
    for (const part of wrapText(a, "F1", fs(11), 250)) { page.text(L, ly, part, "F1", fs(11)); ly -= fs(14); }
  }
  for (const f of model.recipient.fields ?? []) { page.text(L, ly, `${f.label}: ${f.value}`, "F1", fs(9), MUTED); ly -= fs(12); }

  const meta = tx.meta;
  let ry = top;
  const MX = 340;
  for (const [label, value] of meta) {
    const font = label === t.number ? "F2" : "F1";
    page.text(MX, ry, label, "F1", fs(9), MUTED);
    // Praxistest K-060: Ein langer Wert (Referenz der Marke, „Storniert Rechnung …“) überdruckte seine Beschriftung.
    // Passt er nicht mehr neben sie, steht er in eigenen Zeilen darunter, umbrochen auf die Breite des Blocks.
    if (textWidth(label, "F1", fs(9)) + 10 + textWidth(value, font, fs(10)) <= R - MX) {
      page.right(R, ry, value, font, fs(10));
      ry -= fs(15);
    } else {
      ry -= fs(13);
      for (const part of wrapText(value, font, fs(10), R - MX).slice(0, 4)) { page.right(R, ry, part, font, fs(10)); ry -= fs(13); }
      ry -= 2;
    }
  }
  y = Math.min(ly, ry) - 26;

  // ── Überschrift ──
  page.text(L, y, `${titleWord} ${model.number}`.trim(), "F2", fs(14), accent);
  y -= fs(26);

  // ── Positionen ──
  // Spalten der Vorlage: ohne Pos. beginnt die Leistung am Rand; ohne Menge bzw. Einzelpreis steht „3 × 120,00 €“ in der Leistung
  const S = lay.spalten;
  const X_POS = L, X_DESC = S.pos ? L + 30 : L, X_AMT = R;
  const X_UNIT = S.einzelpreis ? R - 88 : R;
  const X_QTY = S.menge ? (S.einzelpreis ? R - 175 : R - 88) : X_UNIT;
  const DESC_W = (S.menge ? X_QTY : S.einzelpreis ? X_UNIT : R - 88) - 40 - X_DESC;
  const kopfFuellung = lay.design === "akzent" ? hellerTon(markenFarbe) : null;
  const header = () => {
    if (kopfFuellung) page.rect(L - 6, y - 7, R - L + 12, fs(9) + 12, kopfFuellung);
    if (S.pos) page.text(X_POS, y, t.pos, "F2", fs(9), MUTED);
    page.text(X_DESC, y, t.item, "F2", fs(9), MUTED);
    if (S.menge) page.right(X_QTY, y, t.qty, "F2", fs(9), MUTED);
    if (S.einzelpreis) page.right(X_UNIT, y, t.unit, "F2", fs(9), MUTED);
    page.right(X_AMT, y, t.amount, "F2", fs(9), MUTED);
    y -= 6;
    if (!kopfFuellung) page.line(L, y, R, y, 0.8, linie);
    y -= 14;
  };
  const newPage = () => {
    page = new Page();
    pages.push(page);
    y = H - 56;
    page.text(L, y, `${titleWord} ${model.number} · ${t.continued}`, "F1", 9, MUTED);
    y -= 24;
  };
  header();
  model.lines.forEach((l, i) => {
    const vorsatz = (!S.menge || !S.einzelpreis) && l.quantity !== 1 ? `${tx.qty(l.quantity)} × ${m(l.unitPriceCents)} · ` : "";
    const desc = wrapText(`${vorsatz}${l.description.trim() || "–"}`, "F1", fs(10), DESC_W);
    const zeile = fs(13);
    const need = desc.length * zeile + 8;
    if (y - need < bottom + 20) { newPage(); header(); }
    if (S.pos) page.text(X_POS, y, String(i + 1), "F1", fs(10), MUTED);
    desc.forEach((part, j) => page.text(X_DESC, y - j * zeile, part, "F1", fs(10)));
    if (S.menge) page.right(X_QTY, y, tx.qty(l.quantity), "F1", fs(10));
    if (S.einzelpreis) page.right(X_UNIT, y, m(l.unitPriceCents), "F1", fs(10));
    page.right(X_AMT, y, m(l.totalCents), "F1", fs(10));
    y -= desc.length * zeile + 4;
    page.line(L, y + 6, R, y + 6, 0.4, RULE);
    y -= 6;
  });

  // ── Summen ──
  const noticeLines = tx.notice ? wrapText(tx.notice, "F1", fs(9.5), R - L) : [];
  // Bankblock nach Vorlage; mit QR-Zahlteil der Schweiz bleibt die Zeile, die Rechnung und Zahlteil verbindet
  const bankRows = lay.bank || tx.qrBill ? tx.bankRows : [];
  const payLines = wrapText(tx.payText, "F1", fs(9.5), R - L);
  void credit;
  const totalsH = (2 + tx.taxRows.length) * 16 + 14;
  if (y - totalsH < bottom + 10) newPage();
  y -= 4;
  // L2: lange Zeilen je Steuersatz („Umsatzsteuer 19 % auf … netto“) rücken den Block nach links
  const widest = Math.max(0, ...tx.taxRows.map(([label]) => textWidth(label, "F1", fs(10))));
  const TX = Math.max(L, Math.min(R - 230, R - 90 - widest));
  page.text(TX, y, t.net, "F1", fs(10), MUTED);
  page.right(R, y, m(model.totals.netCents), "F1", fs(10));
  y -= 16;
  for (const [label, cents] of tx.taxRows) {
    page.text(TX, y, label, "F1", fs(10), MUTED);
    page.right(R, y, m(cents), "F1", fs(10));
    y -= 16;
  }
  y += 16;
  y -= 8;
  if (kopfFuellung) page.rect(TX - 6, y - 20, R - TX + 12, 22, kopfFuellung);
  else page.line(TX, y, R, y, 0.8, linie);
  y -= 14;
  page.text(TX, y, t.gross, "F2", fs(11));
  page.right(R, y, m(model.totals.grossCents), "F2", fs(11));
  y -= 30;

  // ── Steuerhinweis, Zahlungshinweis, Bankverbindung ──
  const block = (lines: string[], font: FontKey = "F1", size = fs(9.5), color: Rgb = INK) => {
    if (y - lines.length * fs(13) < bottom) newPage();
    for (const part of lines) { page.text(L, y, part, font, size, color); y -= fs(13); }
    y -= 6;
  };
  const absaetze = (txt: string | null, color: Rgb = INK) => {
    if (!txt) return;
    const lines = txt.split("\n").flatMap((z) => (z.trim() ? wrapText(z.trim(), "F1", fs(9.5), R - L) : [""]));
    block(lines, "F1", fs(9.5), color);
  };
  if (tx.taxEurText) block(wrapText(tx.taxEurText, "F1", fs(9.5), R - L), "F1", fs(9.5), MUTED);
  if (noticeLines.length) block(noticeLines);
  absaetze(lay.hinweis);
  block(payLines);
  // Girocode (EPC-QR) rechts neben der Bankverbindung: nur Euro, IBAN, Zahlung an den Aussteller, kein Schweizer Zahlteil
  const giro = lay.girocode && lay.bank && !credit && !self && !tx.qrBill && model.bank?.iban
    ? girocodeInhalt({ name: model.bank.accountHolder?.trim() || model.issuer.name, iban: model.bank.iban, bic: model.bank.bic, cents: model.totals.grossCents, currency: model.currency, reference: model.number || null })
    : null;
  const GIRO = 78;
  if (giro) {
    if (y - Math.max(GIRO + 14, (bankRows.length + 1) * fs(13)) < bottom) newPage();
    const q = girocodeModule(giro);
    const s = GIRO / q.size, gx = R - GIRO, gy = y - GIRO + 9;
    page.rects(q.runs.map(([x, yy, w]) => [gx + x * s, gy + (q.size - 1 - yy) * s, w * s, s] as const), INK);
    page.right(R, gy - 10, lang === "en" ? "Scan to pay (GiroCode)" : "Mit der Banking-App scannen", "F1", 7.5, MUTED);
  }
  if (bankRows.length && tx.qrBill) {
    // Mit QR-Zahlteil steht das Konto unten ohnehin: Bankverbindung in einer Zeile, damit Rechnung und Zahlteil auf eine Seite passen
    block(wrapText(`${tx.bankTitle}: ${bankRows.map(([label, value]) => (label === t.holder || label === t.bankName ? value : `${label} ${value}`)).join(" · ")}`, "F1", fs(9.5), R - L));
  } else if (bankRows.length) {
    if (!giro && y - (bankRows.length + 1) * fs(13) < bottom) newPage();
    const start = y;
    page.text(L, y, tx.bankTitle, "F2", fs(9.5));
    y -= fs(13);
    for (const [label, value] of bankRows) {
      page.text(L, y, label, "F1", fs(9.5), MUTED);
      page.text(L + 90, y, value, "F1", fs(9.5));
      y -= fs(13);
    }
    if (giro) y = Math.min(y, start - GIRO - 18);
  } else if (giro) y -= GIRO + 18;
  if (lay.bedingungen) { y -= 6; absaetze(lay.bedingungen, MUTED); }
  if (model.qrBillNote?.trim() && !tx.qrBill && !credit) {
    y -= 6;
    block(wrapText(model.qrBillNote.trim(), "F1", 8.5, R - L), "F1", 8.5, MUTED);
  }

  // ── QR-Zahlteil (Schweiz): unten auf dieser Seite, wenn Platz ist, sonst auf einer eigenen Seite ──
  // Die Fußzeile der Seite mit Zahlteil rückt über die Trennlinie (im Zahlteil darf nichts anderes stehen).
  const slipFooterShift = QR_SLIP_HEIGHT + 13 - (footTop - 12 - 10 * Math.max(0, footer.length - 1));
  let slipPage: Page | null = null;
  if (tx.qrBill) {
    // y ist die nächste freie Grundlinie, die letzte Textzeile steht 19 pt darüber: mindestens 12 pt Abstand zur Fußlinie
    if (y + 19 < footTop + slipFooterShift + 12) newPage();
    slipPage = page;
    drawSwissQrSlip(page, tx.qrBill);
  }

  // ── Fußzeile je Seite (Seitenzahl und Band des Designs „akzent“ nach Vorlage) ──
  pages.forEach((p, i) => {
    const top = footTop + (p === slipPage ? slipFooterShift : 0);
    p.line(L, top, R, top, 0.4, RULE);
    let fy = top - 12;
    for (const f of footer) {
      p.text(L, fy, f, "F1", 7.5, MUTED);
      fy -= 10;
    }
    if (lay.seitenzahlen) p.right(R, top - 12, t.page(i + 1, pages.length), "F1", 7.5, MUTED);
    if (lay.design === "akzent") p.rect(0, H - 10, W, 10, markenFarbe);
  });

  return { pages, title: `${titleWord} ${model.number}`.trim(), images: logo ? [logo] : [] };
}

// ---------------------------------------------------------------------------
// Schweizer QR-Zahlteil mit Empfangsschein (Swiss Implementation Guidelines QR-bill 2.3, Kapitel 3)
// ---------------------------------------------------------------------------

const MM = 72 / 25.4;
/** Höhe des Zahlteils (A6 quer: 105 mm) am unteren Seitenrand */
export const QR_SLIP_HEIGHT = 105 * MM;
const BLACK: Rgb = [0, 0, 0];
const WHITE: Rgb = [1, 1, 1];

/** Eckmarken eines leeren Feldes (Betrag bzw. Zahlungspflichtiger zum Ausfüllen) */
function cornerMarks(p: Page, x: number, y: number, w: number, h: number) {
  const c = 3 * MM, lw = 0.75;
  p.line(x, y + h - c, x, y + h, lw, BLACK); p.line(x, y + h, x + c, y + h, lw, BLACK);
  p.line(x + w - c, y + h, x + w, y + h, lw, BLACK); p.line(x + w, y + h, x + w, y + h - c, lw, BLACK);
  p.line(x, y + c, x, y, lw, BLACK); p.line(x, y, x + c, y, lw, BLACK);
  p.line(x + w - c, y, x + w, y, lw, BLACK); p.line(x + w, y, x + w, y + c, lw, BLACK);
}

/**
 * Zeichnet Empfangsschein (links, 62 mm) und Zahlteil (rechts, 148 mm) in die unteren 105 mm der Seite: Trennlinien mit
 * dem Hinweis „Vor der Einzahlung abzutrennen“, Swiss QR Code 46 × 46 mm mit Schweizer Kreuz (7 mm) in der Mitte,
 * Schrift Helvetica: Titel 11 pt fett, Überschriften 6 pt (Empfangsschein) bzw. 8 pt (Zahlteil) fett, Werte 8 bzw. 10 pt.
 */
export function drawSwissQrSlip(p: Page, bill: SwissQrBill) {
  const lb = QR_BILL_LABELS[bill.language === "en" ? "en" : "de"];
  const top = QR_SLIP_HEIGHT;
  const pad = 5 * MM;
  const px = 62 * MM;
  // Trennlinien und Hinweis (Druck auf Normalpapier: Linie mit Hinweis über der Linie)
  p.line(0, top, W, top, 0.5, BLACK, [3, 2]);
  p.line(px, 0, px, top, 0.5, BLACK, [3, 2]);
  p.text((W - textWidth(lb.separate, "F1", 7)) / 2, top + 4, lb.separate, "F1", 7, BLACK);

  const ref = bill.referenceType === "NON" ? null : qrReferenceDisplay(bill.referenceType, bill.reference);
  const creditorLines = [qrAccountDisplay(bill.account), ...qrAddressLines(bill.creditor)];
  const debtorLines = bill.debtor ? qrAddressLines(bill.debtor) : null;
  const amount = bill.amountCents == null ? null : qrAmountDisplay(bill.amountCents);

  // Spalte mit Überschriften und Werten: cy ist die Oberkante der nächsten Zeile
  const column = (x: number, width: number, head: number, value: number, lh: number) => {
    let cy = 0;
    return {
      at(v: number) { cy = v; },
      get y() { return cy; },
      heading(text: string) { p.text(x, cy - head * 0.8, text, "F2", head, BLACK); cy -= lh; },
      values(lines: string[], max = 99) {
        let used = 0;
        for (const l of lines) {
          for (const part of wrapText(l, "F1", value, width)) {
            if (used >= max) return;
            p.text(x, cy - value * 0.8, part, "F1", value, BLACK);
            cy -= lh;
            used++;
          }
        }
      },
      gap() { cy -= lh; },
    };
  };

  // ── Empfangsschein ──
  p.text(pad, top - pad - 8, lb.receipt, "F2", 11, BLACK);
  const r = column(pad, 52 * MM, 6, 8, 9);
  r.at(top - 12 * MM);
  r.heading(lb.account);
  r.values(creditorLines, 5);
  r.gap();
  if (ref) { r.heading(lb.reference); r.values([ref], 2); r.gap(); }
  if (debtorLines) {
    r.heading(lb.payableBy);
    r.values(debtorLines, 4);
  } else {
    r.heading(lb.payableByBlank);
    cornerMarks(p, pad, r.y - 20 * MM - 1, 52 * MM, 20 * MM);
  }
  // Betrag
  const ra = top - 68 * MM;
  p.text(pad, ra - 6 * 0.8, lb.currency, "F2", 6, BLACK);
  p.text(pad + 12 * MM, ra - 6 * 0.8, lb.amount, "F2", 6, BLACK);
  p.text(pad, ra - 9 - 8 * 0.8, bill.currency, "F1", 8, BLACK);
  if (amount) p.text(pad + 12 * MM, ra - 9 - 8 * 0.8, amount, "F1", 8, BLACK);
  else cornerMarks(p, 62 * MM - pad - 30 * MM, ra - 3 - 10 * MM, 30 * MM, 10 * MM);
  // Annahmestelle
  p.right(62 * MM - pad, 23 * MM - 6 * 0.8, lb.acceptance, "F2", 6, BLACK);

  // ── Zahlteil ──
  p.text(px + pad, top - pad - 8, lb.paymentPart, "F2", 11, BLACK);
  // Swiss QR Code 46 × 46 mm, 17 mm unter der Oberkante, mit Schweizer Kreuz 7 × 7 mm
  const qr = swissQrCode(bill);
  const size = 46 * MM;
  const qx = px + pad, qy = top - 17 * MM - size;
  const s = size / qr.size;
  p.rects(qrRuns(qr).map(([x, y, w]) => [qx + x * s, qy + (qr.size - 1 - y) * s, w * s, s] as const), BLACK);
  const cx = qx + size / 2, cyy = qy + size / 2;
  p.rect(cx - 3.5 * MM, cyy - 3.5 * MM, 7 * MM, 7 * MM, WHITE);
  p.rect(cx - 3 * MM, cyy - 3 * MM, 6 * MM, 6 * MM, BLACK);
  const arm = (7 / 6) * MM, len = (35 / 9) * MM;
  p.rect(cx - arm / 2, cyy - len / 2, arm, len, WHITE);
  p.rect(cx - len / 2, cyy - arm / 2, len, arm, WHITE);
  // Betrag
  const pa = top - 68 * MM;
  p.text(px + pad, pa - 8 * 0.8, lb.currency, "F2", 8, BLACK);
  p.text(px + pad + 16 * MM, pa - 8 * 0.8, lb.amount, "F2", 8, BLACK);
  p.text(px + pad, pa - 11 - 10 * 0.8, bill.currency, "F1", 10, BLACK);
  if (amount) p.text(px + pad + 16 * MM, pa - 11 - 10 * 0.8, amount, "F1", 10, BLACK);
  else cornerMarks(p, px + 16 * MM, pa - 4 - 15 * MM, 40 * MM, 15 * MM);
  // Angaben rechts (87 mm breit)
  const ix = px + 56 * MM;
  const c = column(ix, 87 * MM - pad, 8, 10, 11);
  c.at(top - pad);
  c.heading(lb.account);
  c.values(creditorLines, 6);
  c.gap();
  if (ref) { c.heading(lb.reference); c.values([ref], 2); c.gap(); }
  if (bill.message?.trim()) { c.heading(lb.additional); c.values([bill.message.trim()], 3); c.gap(); }
  if (debtorLines) {
    c.heading(lb.payableBy);
    c.values(debtorLines, 4);
  } else {
    c.heading(lb.payableByBlank);
    cornerMarks(p, ix, c.y - 25 * MM - 1, 65 * MM, 25 * MM);
  }
}

/** Base64 für Mail-Anhänge (ohne Buffer/btoa-Abhängigkeit, Deno/Browser/Node). */
export function bytesToBase64(bytes: Uint8Array): string {
  const A = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const v = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out += A[(v >> 18) & 63]! + A[(v >> 12) & 63]! + A[(v >> 6) & 63]! + A[v & 63]!;
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const v = bytes[i]! << 16;
    out += `${A[(v >> 18) & 63]!}${A[(v >> 12) & 63]!}==`;
  } else if (rest === 2) {
    const v = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out += `${A[(v >> 18) & 63]!}${A[(v >> 12) & 63]!}${A[(v >> 6) & 63]!}=`;
  }
  return out;
}
